/**
 * rAF-coalesced MessageSink (HIG-5964/R4b, design: "R4b, engine-owned").
 *
 * SSE events arrive far faster than frames render; applying each event's
 * patches immediately meant a store write — and a render pass — per event.
 * This sink buffers patches and hands the whole buffer to `apply` at most
 * once per scheduled flush (a frame in production), preserving emission
 * order. `flush()` drains synchronously for the points that need the store
 * current *now*: turn settle, and the temp→real chat-id migration whose
 * direct store writes would otherwise overtake buffered patches still keyed
 * to the temp id.
 */
import type { StorePatch } from './accumulator'
import type { MessageSink } from './services'

/** Schedules one deferred flush; production uses `rafScheduler`, tests fire by hand. */
export type SinkScheduler = (flush: () => void) => void

/**
 * requestAnimationFrame with a timeout fallback: rAF doesn't fire in hidden
 * windows (Electron background throttling), and a background chat's stream
 * must still reach the store — sidebar previews and a later reopen read it.
 */
export const rafScheduler: SinkScheduler = (flush) => {
  let done = false
  const run = () => {
    if (done) return
    done = true
    flush()
  }
  requestAnimationFrame(run)
  setTimeout(run, 100)
}

export function createCoalescingSink(
  apply: (patches: ReadonlyArray<StorePatch>) => void,
  schedule: SinkScheduler,
): MessageSink {
  let buffer: Array<StorePatch> = []
  let scheduled = false

  const flush = (): void => {
    scheduled = false
    if (buffer.length === 0) return
    const patches = buffer
    buffer = []
    apply(patches)
  }

  return {
    applyAll: (patches) => {
      if (patches.length === 0) return
      buffer.push(...patches)
      if (!scheduled) {
        scheduled = true
        schedule(flush)
      }
    },
    flush,
  }
}
